import React, { useState, useEffect } from 'react';
import { 
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import ViewTransactionModal from '../components/Modals/ViewTransactionModal.js';
import EditTransactionModal from '../components/Modals/EditTransactionModal.js';
import AddTransactionModal from '../components/Modals/AddTransactionModal.js';
import ConfirmModal from '../components/Modals/ConfirmModal';
import '../styles/transactions.css';
import { FiSearch, FiEye, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { authFetch } from '../utils/api';

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, transactionId: null });

  // Fetch transactions from backend on mount
  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const res = await authFetch('/transactions');
        if (res.ok) {
          const data = await res.json();
          setTransactions(data);
        }
      } catch (err) {
        // handle error
      }
    };
    fetchTransactions();
  }, []);

  // Filter transactions whenever filters change
  useEffect(() => {
    let filtered = transactions;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(transaction => 
        (transaction.transaction_public_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (transaction.customer || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (transaction.description || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (selectedStatus !== 'All') {
      filtered = filtered.filter(transaction => 
        (transaction.status || '').toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    // Apply date filters
    if (startDate && endDate) {
      filtered = filtered.filter(transaction => {
        const transactionDate = new Date(transaction.date);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return transactionDate >= start && transactionDate <= end;
      });
    }

    setFilteredTransactions(filtered);
  }, [transactions, searchTerm, selectedStatus, startDate, endDate]);

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setViewModalOpen(true);
  };

  const handleEditTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setEditModalOpen(true);
  };

  const handleCreateTransaction = () => {
    console.log('Add Transaction button clicked');
    setAddModalOpen(true);
  };

  const handleDeleteTransaction = (id) => {
    setConfirmDelete({ open: true, transactionId: id });
  };

  const confirmDeleteTransaction = async () => {
    const id = confirmDelete.transactionId;
    setConfirmDelete({ open: false, transactionId: null });
      try {
        const res = await authFetch(`/transactions/${id}`, { method: 'DELETE' });
        if (res.ok) {
          setTransactions(prev => prev.filter(t => t.transaction_id !== id));
        }
      } catch (err) {}
  };

  const handleSaveTransaction = async (transactionData) => {
    try {
      // Update existing transaction
      const res = await authFetch(`/transactions/${transactionData.transaction_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transactionData)
      });
      if (res.ok) {
        const updated = await res.json();
        setTransactions(prev => prev.map(t => t.transaction_id === updated.transaction_id ? updated : t));
      }
    } catch (err) {}
    setEditModalOpen(false);
    setSelectedTransaction(null);
  };

  const handleAddTransaction = async (transactionData) => {
    try {
      // Create new transaction
              const res = await authFetch('/transactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transactionData)
      });
      if (res.ok) {
        const newTransaction = await res.json();
        setTransactions(prev => [...prev, newTransaction]);
      }
    } catch (err) {}
    setAddModalOpen(false);
  };

  return (
    <main className="page-container">
      <div className="page-header">
        <h1>Transactions</h1>
        <button className="btn btn-primary" onClick={handleCreateTransaction}>+ NEW TRANSACTION</button>
      </div>

      <div className="filters-container">
        <div className="search-container">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search transactions..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          className="form-input"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="completed">Completed</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>

        <input
          type="date"
          className="form-input"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          placeholder="Start Date"
        />

        <input
          type="date"
          className="form-input"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          placeholder="End Date"
        />
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>TRANSACTION ID</th>
              <th>CUSTOMER</th>
              <th>AMOUNT</th>
              <th>STATUS</th>
              <th>DATE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map((transaction) => (
              <tr key={transaction.transaction_id}>
                <td>{transaction.transaction_public_id}</td>
                <td>{transaction.customer}</td>
                <td className={transaction.type === 'credit' ? 'text-success' : 'text-danger'}>
                  {transaction.type === 'credit' ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                </td>
                <td>
                  <span className={`status-badge ${transaction.status.toLowerCase()}`}>
                    {transaction.status}
                  </span>
                </td>
                <td>{transaction.date}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="action-button"
                      onClick={() => handleViewTransaction(transaction)}
                    >
                      <FiEye />
                    </button>
                    <button 
                      className="action-button"
                      onClick={() => handleEditTransaction(transaction)}
                    >
                      <FiEdit2 />
                    </button>
                    <button 
                      className="action-button"
                      onClick={() => handleDeleteTransaction(transaction.transaction_id)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredTransactions.length === 0 && (
        <div className="empty-state">
          <ReceiptIcon className="empty-state-icon" />
          <p className="empty-state-text">No transactions found</p>
          <button 
            className="btn btn-primary"
            onClick={handleCreateTransaction}
          >
            <AddIcon />
            Create Transaction
          </button>
        </div>
      )}

      <ViewTransactionModal
        show={viewModalOpen}
        transaction={selectedTransaction}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedTransaction(null);
        }}
        onEdit={() => {
          setViewModalOpen(false);
          handleEditTransaction(selectedTransaction);
        }}
      />

      <EditTransactionModal
        show={editModalOpen}
        transaction={selectedTransaction}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedTransaction(null);
        }}
        onSave={handleSaveTransaction}
      />

      <AddTransactionModal
        show={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddTransaction}
      />

      <ConfirmModal
        show={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, transactionId: null })}
        onConfirm={confirmDeleteTransaction}
        title="Delete Transaction?"
        message="Are you sure you want to delete this transaction? This action cannot be undone."
        confirmText="Delete"
        confirmColor="btn-danger"
      />
    </main>
  );
};

export default TransactionsPage; 